"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TextArea = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _noop = require("../../constants/noop");
var _useTheme = require("../../theme/utils/useTheme");
var _focusMode = require("../focusMode");
var _formInputTheme = require("../textinput/styled/formInputTheme");
var _constants = require("./constants");
var _GrowWrapperStyled = require("./styled/GrowWrapperStyled");
var _StyledContentAfterContainer = require("./styled/StyledContentAfterContainer");
var _StyledResizer = require("./styled/StyledResizer");
var _StyledTextAreaRoot = require("./styled/StyledTextAreaRoot");
var _TextAreaStyled = require("./styled/TextAreaStyled");
const TextArea = /*#__PURE__*/(0, _react.forwardRef)((props, parentRef) => {
  const {
    disableManualResize = false,
    value,
    onChange,
    minHeight,
    maxHeight,
    contentAfter,
    clientId,
    isDisabled = false,
    style: propStyle,
    validationState,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyUp,
    onMouseEnter,
    onMouseLeave,
    textAreaStyles,
    ...htmlAttributes
  } = props;
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  const [wrapperValue, setWrapperValue] = (0, _react.useState)(value);
  const cursorRef = (0, _react.useRef)();
  const textAreaRef = (0, _lodestarUtils.useEnsuredForwardedRef)(parentRef);
  const wrapperRef = (0, _react.useRef)(null);
  const borderWidth = _formInputTheme.formInputTheme.borderWidth[(0, _formInputTheme.getStylingState)({
    validationState,
    isFocused,
    focusMode
  })](themeMode);
  const topPadding = _formInputTheme.formInputTheme.padding.top[(0, _formInputTheme.getStylingState)({
    validationState,
    isFocused,
    focusMode
  })](themeMode);
  const bottomPadding = _formInputTheme.formInputTheme.padding.bottom[(0, _formInputTheme.getStylingState)({
    validationState,
    isFocused,
    focusMode
  })](themeMode);
  const bordersPlusPadding = borderWidth * 2 + topPadding + bottomPadding;
  const absoluteMinHeight = bordersPlusPadding + Math.floor(_StyledTextAreaRoot.LINE_HEIGHT_PX);
  const calculatedMinHeight = typeof minHeight === "number" ? `${minHeight * _formInputTheme.formInputTheme.lineHeight(themeMode) + bordersPlusPadding}px` : minHeight || `${absoluteMinHeight}px`;
  const calculatedMaxHeight = typeof maxHeight === "number" ? `${maxHeight * _formInputTheme.formInputTheme.lineHeight(themeMode) + bordersPlusPadding}px` : maxHeight;
  function handleOnChange(e) {
    setWrapperValue(e.target.value);
    if (onChange) {
      onChange(e.target.value, e);
    }
  }
  const handleMouseMove = (0, _react.useCallback)(e => {
    if (textAreaRef.current && wrapperRef.current) {
      // Get the top position of the wrapper element relative to the viewport
      const {
        top
      } = wrapperRef.current.getBoundingClientRect();
      // Adjust for any vertical scrolling
      const offsetTop = top + window.scrollY;
      // Calculate the new height based on the current mouse Y-coordinate
      const height = e.pageY - offsetTop;
      // Apply the calculated height to the wrapper element
      wrapperRef.current.style.height = `${height}px`;
    }
  }, [wrapperRef, textAreaRef]);
  const handleKeyDown = (0, _lodestarUtils.useIsComposingHandler)(onKeyDown || _noop.noop);
  const handleKeyUp = (0, _lodestarUtils.useIsComposingHandler)(onKeyUp || _noop.noop);
  const handleCleanUp = (0, _react.useCallback)(() => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseMove);
    // reset the cursor style when user drag is done. This does not appear to affect styles set higher in the dom tree.
    if (document?.body) {
      document.body.style.cursor = cursorRef.current || "auto";
    }
  }, [handleMouseMove]);
  const handleMouseDown = e => {
    // necessary to prevent highlighting the text when resizing
    e.preventDefault();
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleCleanUp);
    if (document?.body) {
      // since we've manufactured a resizer, we need to know when we're dragging (since a user can drag off of the target, losing css hover state)
      document.body.style.cursor = "ns-resize";
    }
  };
  (0, _react.useEffect)(() => {
    // we need to grab this value in case it isn't "auto", so we can reset it later
    if (document?.body) {
      const initialCursor = document.body.style.cursor;
      cursorRef.current = initialCursor;
    }
    return () => {
      handleCleanUp();
    };
  }, [handleCleanUp]);
  return /*#__PURE__*/_react.default.createElement(_core.ClassNames, null, _ref => {
    let {
      cx
    } = _ref;
    return /*#__PURE__*/_react.default.createElement(_StyledTextAreaRoot.StyledTextAreaRoot, {
      themeMode: themeMode,
      isDisabled: isDisabled,
      isFocused: isFocused,
      ref: wrapperRef,
      isHovered: isHovered,
      focusMode: focusMode,
      onClick: () => {
        !isDisabled && textAreaRef.current?.focus();
      },
      style: {
        maxHeight: calculatedMaxHeight,
        minHeight: `max(${absoluteMinHeight}px, ${calculatedMinHeight})`,
        ...propStyle
      },
      validationState: validationState,
      hasContentAfter: !!contentAfter,
      className: htmlAttributes.className
    }, !disableManualResize && !isDisabled && /*#__PURE__*/_react.default.createElement(_StyledResizer.StyledResizer, {
      themeMode: themeMode,
      isFocused: isFocused,
      focusMode: focusMode,
      validationState: validationState,
      onMouseDown: handleMouseDown,
      onMouseUp: handleCleanUp
    }), /*#__PURE__*/_react.default.createElement(_GrowWrapperStyled.GrowWrapperStyled, {
      themeMode: themeMode,
      isFocused: isFocused,
      validationState: validationState,
      focusMode: focusMode
    }, /*#__PURE__*/_react.default.createElement(_TextAreaStyled.TextAreaStyled, (0, _extends2.default)({
      isDisabled: isDisabled,
      isFocused: isFocused,
      isHovered: isHovered,
      focusMode: focusMode
      // this is a default, which will be overridden by content, minHeight, maxHeight
      ,
      rows: 4,
      maxHeight: calculatedMaxHeight ? `calc(${calculatedMaxHeight} - ${borderWidth * 2}px)` : undefined,
      minHeight: `calc(${calculatedMinHeight} - ${borderWidth * 2}px)`,
      value: value,
      onChange: handleOnChange,
      onKeyDown: onKeyDown && handleKeyDown || undefined,
      onKeyUp: onKeyUp && handleKeyUp || undefined,
      onFocus: (0, _lodestarUtils.callAllHandlers)(() => setIsFocused(true), onFocus),
      onBlur: (0, _lodestarUtils.callAllHandlers)(() => setIsFocused(false), onBlur),
      onMouseEnter: (0, _lodestarUtils.callAllHandlers)(() => setIsHovered(true), onMouseEnter),
      onMouseLeave: (0, _lodestarUtils.callAllHandlers)(() => setIsHovered(false), onMouseLeave),
      ref: textAreaRef,
      validationState: validationState,
      "data-client-id": clientId,
      themeMode: themeMode,
      "aria-disabled": isDisabled
    }, htmlAttributes, {
      style: textAreaStyles,
      readOnly: isDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false
    })), /*#__PURE__*/_react.default.createElement("div", {
      className: cx(_constants.GROW_WRAPPER_CLASS, htmlAttributes.className),
      style: textAreaStyles
    }, wrapperValue, "\xA0")), contentAfter && /*#__PURE__*/_react.default.createElement(_StyledContentAfterContainer.StyledContentAfterContainer, {
      themeMode: themeMode,
      isFocused: isFocused,
      focusMode: focusMode,
      validationState: validationState
    }, contentAfter));
  });
});
exports.TextArea = TextArea;
TextArea.displayName = "TextArea";
//# sourceMappingURL=TextArea.js.map